import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import type { Href } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Platform, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';

import { FlexModal } from '@/components/flex-modal';
import {
	detailNum,
	type GlDetailLine,
	type GlDetailSortKey,
	isPlAccountType,
	naturalBalanceSign,
	sortGlDetailLines,
	sourceLabel,
} from '@/components/reports/gl-detail-sort';
import { useGlReport } from '@/hooks/use-gl-report';
import type { GlReportAccount } from '@/services/accounting-service';
import { formatBusinessDate } from '@/utils/date-utils';
import { formatMoneyCents } from '@/utils/format-money';

interface GlDetailModalProps {
	visible: boolean;
	onDismiss: () => void;
	associationId: string;
	/** The GL code's stable id — used to find the account in the report payload. */
	glCodeId: string;
	/** The numeric/text code shown in the report (e.g. "1100"). For header display. */
	glCode?: string;
	/** Human label (e.g. "Accounts Receivable"). For header display. */
	glName?: string;
	/** Window start — ms since epoch. Balance Sheet passes 1 (≈ epoch). P&L passes period start. */
	startDate: number;
	/** Window end — ms since epoch. */
	endDate: number;
	/** Accounting basis. Balance Sheet may pass either; P&L always passes 'accrual' for now. */
	basis: 'cash' | 'accrual';
	/** Optional label shown in the header (e.g. "As of April 30, 2026" or "Apr 1 – Apr 30, 2026"). */
	windowLabel?: string;
}

/**
 * Modal that shows every ledger line that rolls up into a single GL account
 * balance within a given window. Designed for drill-down from tree reports
 * (Balance Sheet, P&L) — click an account row, see its underlying postings
 * without losing the report context.
 *
 * The hook reuses the GL Report cache, so opening the modal for a second
 * account in the same period is near-instant.
 */
export const GlDetailModal: React.FC<GlDetailModalProps> = ({
	visible,
	onDismiss,
	associationId,
	glCodeId,
	glCode,
	glName,
	startDate,
	endDate,
	basis,
	windowLabel,
}) => {
	const queryParams = useMemo(
		() => (visible ? { associationId, startDate, endDate, basis } : undefined),
		[visible, associationId, startDate, endDate, basis],
	);

	const { data, isLoading, error } = useGlReport(queryParams);

	const account = useMemo<GlReportAccount | null>(() => {
		if (!data) return null;
		return data.accounts.find((a) => a.glCodeId === glCodeId) ?? null;
	}, [data, glCodeId]);

	// Display every balance in the account's NATURAL orientation. The GL report
	// returns debit-normal figures (debit − credit), so a credit-normal account
	// (income/liability/equity) comes back negative; flip the sign so it reads
	// positive, matching the P&L. Applied to the running balance AND the
	// opening/ending stats below.
	const balanceSign = account ? naturalBalanceSign(account.accountType) : 1;

	// P&L (income/expense) accounts are flow accounts — no opening/running balance
	// is meaningful, so the modal hides that UI and shows the period Net (the
	// figure shown on the P&L) instead. Balance Sheet accounts keep the balance.
	const isPl = account ? isPlAccountType(account.accountType) : false;
	const periodNetCents = account ? (account.periodDebitCents - account.periodCreditCents) * balanceSign : 0;

	const lines = useMemo<GlDetailLine[]>(() => {
		if (!account) return [];
		const sign = naturalBalanceSign(account.accountType);
		const out: GlDetailLine[] = [];
		let running = account.openingBalanceCents; // debit-normal accumulation
		for (const l of account.lines) {
			running += l.debitCents - l.creditCents;
			out.push({ ...l, balanceCents: running * sign }); // natural for display + sort
		}
		return out;
	}, [account]);

	// Client-side sort over the full period (the modal loads every line). Default
	// is posted date DESCENDING — newest-first, matching the journal report — and
	// that default is shown in the header arrow from first render. The running
	// balance is computed oldest→newest above, so each row keeps its own correct
	// post-posting balance regardless of the chosen sort.
	const [sortKey, setSortKey] = useState<GlDetailSortKey>('postedOn');
	const [sortAsc, setSortAsc] = useState(false);
	const displayLines = useMemo<GlDetailLine[]>(
		() => sortGlDetailLines(lines, sortKey, sortAsc),
		[lines, sortKey, sortAsc],
	);

	const onSort = (key: GlDetailSortKey) => {
		if (key === sortKey) { setSortAsc((p) => !p); return; }
		setSortKey(key);
		// Dates/amounts default to descending (most recent / largest first);
		// text columns default to ascending (A→Z).
		setSortAsc(key === 'name' || key === 'memo' || key === 'type');
	};

	const headerCode = glCode ?? account?.code ?? '';
	const headerName = glName ?? account?.name ?? 'GL detail';

	const onOpenFullGlReport = () => {
		const sp = new URLSearchParams();
		sp.set('startDate', String(startDate));
		sp.set('endDate', String(endDate));
		sp.set('basis', basis);
		const href = `/associations/${associationId}/gl-report?${sp.toString()}` as Href;
		onDismiss();
		router.push(href);
	};

	return (
		<FlexModal visible={visible} onDismiss={onDismiss} fullScreenBreakpoint={900}>
			<View style={styles.header}>
				<View style={styles.headerText}>
					<Text style={styles.title} numberOfLines={2}>
						{headerCode ? `${headerCode} · ` : ''}{headerName}
					</Text>
					{windowLabel ? <Text style={styles.subtitle}>{windowLabel}</Text> : null}
				</View>
				<Pressable onPress={onDismiss} accessibilityRole="button" accessibilityLabel="Close detail">
					<Ionicons name="close" size={22} color="#546e7a" />
				</Pressable>
			</View>

			{account ? (
				<View style={styles.summaryRow}>
					{isPl ? (
						<>
							<SummaryStat label="Debits" amount={account.periodDebitCents} />
							<SummaryStat label="Credits" amount={account.periodCreditCents} />
							<SummaryStat label="Net" amount={periodNetCents} emphasis />
						</>
					) : (
						<>
							<SummaryStat label="Opening" amount={account.openingBalanceCents * balanceSign} />
							<SummaryStat label="Debits" amount={account.periodDebitCents} />
							<SummaryStat label="Credits" amount={account.periodCreditCents} />
							<SummaryStat label="Ending" amount={account.endingBalanceCents * balanceSign} emphasis />
						</>
					)}
				</View>
			) : null}

			{error ? (
				<Text style={styles.warn}>Failed to load lines: {error.message}</Text>
			) : isLoading && !data ? (
				<View style={styles.center}><ActivityIndicator /></View>
			) : !account ? (
				<Text style={styles.empty}>
					No activity for this account in the selected window.
				</Text>
			) : lines.length === 0 ? (
				<Text style={styles.empty}>
					{isPl
						? 'No postings for this account in the selected period.'
						: `Opening balance is ${formatMoneyCents(account.openingBalanceCents * balanceSign)}; no postings in this window.`}
				</Text>
			) : (
				<ScrollView style={styles.tableScroll} contentContainerStyle={styles.tableContent}>
					<View style={[styles.row, styles.rowHead]}>
						<SortHeader label="Date" columnKey="postedOn" activeKey={sortKey} asc={sortAsc} onPress={onSort} cellStyle={styles.cellDate} />
						<SortHeader label="Type" columnKey="type" activeKey={sortKey} asc={sortAsc} onPress={onSort} cellStyle={styles.cellType} />
						<SortHeader label="Num" columnKey="num" activeKey={sortKey} asc={sortAsc} onPress={onSort} cellStyle={styles.cellNum} />
						<SortHeader label="Name" columnKey="name" activeKey={sortKey} asc={sortAsc} onPress={onSort} cellStyle={styles.cellName} />
						<SortHeader label="Memo" columnKey="memo" activeKey={sortKey} asc={sortAsc} onPress={onSort} cellStyle={styles.cellMemo} />
						<SortHeader label="Debit" columnKey="debit" activeKey={sortKey} asc={sortAsc} onPress={onSort} cellStyle={styles.cellAmt} right />
						<SortHeader label="Credit" columnKey="credit" activeKey={sortKey} asc={sortAsc} onPress={onSort} cellStyle={styles.cellAmt} right />
						{!isPl && (
							<SortHeader label="Balance" columnKey="balance" activeKey={sortKey} asc={sortAsc} onPress={onSort} cellStyle={styles.cellAmt} right />
						)}
					</View>

					{displayLines.map((l, idx) => (
						<View key={`${l.sourceKind}:${l.sourceId}:${idx}`} style={styles.row}>
							<Text style={[styles.cell, styles.cellDate]}>{formatBusinessDate(l.postedOn)}</Text>
							<Text style={[styles.cell, styles.cellType]}>{sourceLabel(l.sourceKind)}</Text>
							<Text style={[styles.cell, styles.cellNum]}>{detailNum(l)}</Text>
							<Text style={[styles.cell, styles.cellName]} numberOfLines={1}>{l.counterparty ?? ''}</Text>
							<Text style={[styles.cell, styles.cellMemo]} numberOfLines={2}>{l.memo ?? ''}</Text>
							<Text style={[styles.cell, styles.cellAmt, styles.cellRight]}>
								{l.debitCents > 0 ? formatMoneyCents(l.debitCents) : ''}
							</Text>
							<Text style={[styles.cell, styles.cellAmt, styles.cellRight]}>
								{l.creditCents > 0 ? formatMoneyCents(l.creditCents) : ''}
							</Text>
							{!isPl && (
								<Text style={[styles.cell, styles.cellAmt, styles.cellRight]}>
									{formatMoneyCents(l.balanceCents)}
								</Text>
							)}
						</View>
					))}

					{!isPl && (
						<View style={[styles.row, styles.rowOpening]}>
							<Text style={[styles.cell, styles.cellDate]}>{formatBusinessDate(startDate)}</Text>
							<Text style={[styles.cell, styles.cellType, styles.cellMuted]}>—</Text>
							<Text style={[styles.cell, styles.cellNum]} />
							<Text style={[styles.cell, styles.cellName]} />
							<Text style={[styles.cell, styles.cellMemo, styles.cellMuted]}>Beginning Balance</Text>
							<Text style={[styles.cell, styles.cellAmt, styles.cellRight]} />
							<Text style={[styles.cell, styles.cellAmt, styles.cellRight]} />
							<Text style={[styles.cell, styles.cellAmt, styles.cellRight, styles.cellBold]}>
								{formatMoneyCents(account.openingBalanceCents * balanceSign)}
							</Text>
						</View>
					)}
				</ScrollView>
			)}

			<View style={styles.footer}>
				<Pressable onPress={onOpenFullGlReport} style={styles.secondaryBtn}>
					<Ionicons name="open-outline" size={16} color="#1976d2" />
					<Text style={styles.secondaryBtnLabel}>Open in GL Report</Text>
				</Pressable>
				<Pressable onPress={onDismiss} style={styles.primaryBtn}>
					<Text style={styles.primaryBtnLabel}>Close</Text>
				</Pressable>
			</View>
		</FlexModal>
	);
};

const SortHeader: React.FC<{
	label: string;
	columnKey: GlDetailSortKey;
	activeKey: GlDetailSortKey;
	asc: boolean;
	onPress: (k: GlDetailSortKey) => void;
	cellStyle: object;
	right?: boolean;
}> = ({ label, columnKey, activeKey, asc, onPress, cellStyle, right }) => {
	const active = columnKey === activeKey;
	return (
		<Pressable
			onPress={() => onPress(columnKey)}
			accessibilityRole="button"
			accessibilityLabel={`Sort by ${label}${active ? (asc ? ' ascending' : ' descending') : ''}`}
			style={[cellStyle, styles.headPressable, right && styles.headPressableRight]}
		>
			<Text style={[styles.cellHead, active && styles.cellHeadActive]} numberOfLines={1}>{label}</Text>
			{/* Arrow reserves space even when inactive so column widths don't jump. */}
			<Text style={[styles.sortArrow, !active && styles.sortArrowHidden]}>{asc ? '▲' : '▼'}</Text>
		</Pressable>
	);
};

const SummaryStat: React.FC<{ label: string; amount: number; emphasis?: boolean }> = ({
	label,
	amount,
	emphasis,
}) => (
	<View style={styles.stat}>
		<Text style={styles.statLabel}>{label}</Text>
		<Text style={[styles.statAmt, emphasis && styles.statAmtEmphasis]}>
			{formatMoneyCents(amount)}
		</Text>
	</View>
);

const styles = StyleSheet.create({
	header: {
		flexDirection: 'row',
		alignItems: 'flex-start',
		gap: 12,
		marginBottom: 12,
	},
	headerText: { flex: 1, minWidth: 0 },
	title: { fontSize: 18, fontWeight: '700', color: '#212121' },
	subtitle: { fontSize: 12, color: '#546e7a', marginTop: 2 },

	summaryRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 16,
		paddingVertical: 10,
		paddingHorizontal: 12,
		backgroundColor: '#f5f7fa',
		borderRadius: 6,
		marginBottom: 12,
	},
	stat: { flexGrow: 1, flexBasis: 110, minWidth: 90 },
	statLabel: {
		fontSize: 10, fontWeight: '700', color: '#546e7a',
		textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2,
	},
	statAmt: { fontSize: 14, color: '#212121', fontVariant: ['tabular-nums'] },
	statAmtEmphasis: { fontWeight: '700' },

	tableScroll: {
		maxHeight: 480,
		borderWidth: 1,
		borderColor: '#e0e7ef',
		borderRadius: 6,
		...Platform.select({
			web: { overflow: 'auto' as 'scroll' } as object,
			default: {},
		}),
	},
	tableContent: { paddingBottom: 4 },

	row: {
		flexDirection: 'row',
		alignItems: 'center',
		paddingHorizontal: 10,
		paddingVertical: 6,
		borderBottomWidth: 1,
		borderBottomColor: '#eceff1',
		gap: 8,
	},
	rowHead: {
		backgroundColor: '#eceff1',
		borderBottomColor: '#cfd8dc',
	},
	rowOpening: { backgroundColor: '#fafafa' },
	cell: { fontSize: 12, color: '#222' },
	cellHead: { fontSize: 12, fontWeight: '700', color: '#37474f', textTransform: 'uppercase', letterSpacing: 0.3 },
	cellHeadActive: { color: '#1976d2' },
	headPressable: { flexDirection: 'row', alignItems: 'center', gap: 3 },
	headPressableRight: { justifyContent: 'flex-end' },
	sortArrow: { fontSize: 9, color: '#1976d2' },
	sortArrowHidden: { opacity: 0 },
	cellRight: { textAlign: 'right' },
	cellMuted: { color: '#90a4ae' },
	cellBold: { fontWeight: '700' },
	cellDate: { width: 90 },
	cellType: { width: 90 },
	cellNum: { width: 80 },
	cellName: { flex: 1, minWidth: 100 },
	cellMemo: { flex: 2, minWidth: 140 },
	cellAmt: { width: 92, fontVariant: ['tabular-nums'] },

	footer: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginTop: 16,
		gap: 12,
		flexWrap: 'wrap',
	},
	secondaryBtn: {
		flexDirection: 'row', alignItems: 'center', gap: 6,
		paddingHorizontal: 12, paddingVertical: 9, borderRadius: 6,
		borderWidth: 1, borderColor: '#1976d2', backgroundColor: '#fff',
	},
	secondaryBtnLabel: { color: '#1976d2', fontWeight: '600' },
	primaryBtn: {
		paddingHorizontal: 20, paddingVertical: 10, borderRadius: 6,
		backgroundColor: '#1976d2',
	},
	primaryBtnLabel: { color: '#fff', fontWeight: '600' },

	warn: { color: '#c62828', fontSize: 13, paddingVertical: 12 },
	empty: {
		color: '#888', fontStyle: 'italic', textAlign: 'center',
		paddingVertical: 24,
	},
	center: { padding: 32, alignItems: 'center' },
});

export default GlDetailModal;
